import { useState, useRef, useEffect } from 'react';
import { ViewType, GameType, Artifact } from './types';
import { INITIAL_ARTIFACTS } from './data/artifacts';
import { Header } from './components/Header';
import { MobileNav } from './components/MobileNav';
import { Footer } from './components/Footer';
import { Toast } from './components/Toast';
import { ArtifactModal } from './components/ArtifactModal';
import { QRScannerModal } from './components/QRScannerModal';
import { LeaderboardModal } from './components/LeaderboardModal';
import { GameContainerModal } from './games/GameContainerModal';

import { HomeView } from './views/HomeView';
import { CollectionView } from './views/CollectionView';
import { SummaryView } from './views/SummaryView';

export function App() {
  const [currentView, setCurrentView] = useState<ViewType>(() => {
    const params = new URLSearchParams(window.location.search);
    const v = params.get('view');
    if (v === 'journey' || v === 'summary') return v;
    return 'home';
  });
  const [artifacts, setArtifacts] = useState<Artifact[]>(INITIAL_ARTIFACTS);

  // Video & audio state for Hero
  const [isVideoPlaying, setIsPlayingVideo] = useState<boolean>(true);
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Modals state
  const [selectedArtifact, setSelectedArtifact] = useState<Artifact | null>(null);
  const [isScannerOpen, setIsScannerOpen] = useState<boolean>(false);
  const [activeGame, setActiveGame] = useState<GameType | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get('game');
    if (g === 'draw' || g === 'tower' || g === 'puzzle') return g as GameType;
    return null;
  });
  const [gameMode, setGameMode] = useState<'normal' | 'competition'>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') === 'competition' ? 'competition' : 'normal';
  });

  // Leaderboard modal state
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState<boolean>(false);
  const [leaderboardGame, setLeaderboardGame] = useState<'tower' | 'competition' | 'draw' | 'puzzle'>('tower');

  // Toast state
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const handleToggleVideo = () => {
    if (!videoRef.current) return;
    if (isVideoPlaying) {
      videoRef.current.pause();
      setIsPlayingVideo(false);
      document.body.classList.add('video-static');
    } else {
      videoRef.current.play();
      setIsPlayingVideo(true);
      document.body.classList.remove('video-static');
    }
  };

  const handleToggleSound = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleNavigate = (view: ViewType) => {
    setCurrentView(view);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScrollToGame = (game: 'tower' | 'draw' | 'puzzle') => {
    const targetId = `game-${game}`;
    if (currentView !== 'home') {
      setCurrentView('home');
      setTimeout(() => {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          el.classList.add('ring-2', 'ring-[#ffd700]');
          setTimeout(() => el.classList.remove('ring-2', 'ring-[#ffd700]'), 2000);
        }
      }, 150);
    } else {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('ring-2', 'ring-[#ffd700]');
        setTimeout(() => el.classList.remove('ring-2', 'ring-[#ffd700]'), 2000);
      }
    }
  };

  const handleOpenGame = (game: GameType, mode: 'normal' | 'competition' = 'normal') => {
    setActiveGame(game);
    setGameMode(mode);
  };

  const handleOpenLeaderboard = (game: 'tower' | 'competition' | 'draw' | 'puzzle' = 'tower') => {
    setLeaderboardGame(game);
    setIsLeaderboardOpen(true);
  };

  const handleUnlockStamp = (id: number) => {
    setArtifacts((prev) =>
      prev.map((art) => (art.id === id ? { ...art, unlocked: true } : art))
    );
    setToastMessage('Chúc mừng! Bạn đã mở khóa thêm 01 con tem Quốc bảo mới!');
  };

  const handleViewStamp = (id: number) => {
    const art = artifacts.find((a) => a.id === id);
    if (art) {
      setSelectedArtifact(art);
    }
  };

  // Ensure body tag has v2 data attributes
  useEffect(() => {
    document.documentElement.dataset.theme = 'v2';
    document.body.setAttribute('data-version', '2');
    document.body.setAttribute('data-demo-data', 'true');
  }, []);

  return (
    <div className="min-h-screen flex flex-col justify-between bg-[#231419] text-[#fff4dd]">
      {/* Fixed Global Header */}
      <Header
        currentView={currentView}
        onNavigate={handleNavigate}
        onScrollToGame={handleScrollToGame}
      />

      {/* Main View Area (with top padding on non-home views to offset fixed header) */}
      <div className={`flex-1 w-full ${currentView !== 'home' ? 'pt-[68px] md:pt-[82px]' : ''}`}>
        {currentView === 'home' && (
          <HomeView
            onNavigate={handleNavigate}
            onOpenGame={handleOpenGame}
            onOpenLeaderboard={handleOpenLeaderboard}
            onScrollToGame={handleScrollToGame}
            isVideoPlaying={isVideoPlaying}
            onToggleVideo={handleToggleVideo}
            isMuted={isMuted}
            onToggleSound={handleToggleSound}
            videoRef={videoRef}
          />
        )}

        {currentView === 'journey' && (
          <CollectionView
            artifacts={artifacts}
            onOpenArtifact={setSelectedArtifact}
            onOpenScanner={() => setIsScannerOpen(true)}
          />
        )}

        {currentView === 'summary' && (
          <SummaryView
            artifacts={artifacts}
            onNavigate={handleNavigate}
            onShowToast={setToastMessage}
          />
        )}
      </div>

      {/* Footer on inner views */}
      {currentView !== 'home' && <Footer />}

      {/* Mobile Bottom Navigation */}
      <MobileNav currentView={currentView} onNavigate={handleNavigate} />

      {/* Toast notifications */}
      <Toast message={toastMessage} onClose={() => setToastMessage(null)} />

      {/* Artifact Details Modal */}
      <ArtifactModal
        artifact={selectedArtifact}
        onClose={() => setSelectedArtifact(null)}
        onOpenScanner={() => setIsScannerOpen(true)}
      />

      {/* QR Scanner Camera Modal */}
      <QRScannerModal
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onStampUnlocked={handleUnlockStamp}
        onViewStamp={handleViewStamp}
      />

      {/* Top 100 Leaderboard Modal */}
      <LeaderboardModal
        isOpen={isLeaderboardOpen}
        initialGame={leaderboardGame}
        onClose={() => setIsLeaderboardOpen(false)}
      />

      {/* Fullscreen Interactive Game Modal */}
      <GameContainerModal
        game={activeGame}
        mode={gameMode}
        onClose={() => setActiveGame(null)}
        onOpenLeaderboard={(g) => handleOpenLeaderboard((g as any) || 'tower')}
      />
    </div>
  );
}

export default App;
